import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, CheckCircle, XCircle, User, Building2, ArrowLeft } from 'lucide-react';
import { Client } from '../../types';
import type { paths } from '../../api/paths';
import { $api } from '../../api';
import { useOrganization } from '../../contexts/OrganizationContext';
import ErrorMessage from '../ErrorMessage';
import SuccessMessage from '../SuccessMessage';

// Extract alert type from API definitions
type AlertFromAPI = paths['/api/organization/{organization_id}/alerts']['get']['responses']['200']['content']['application/json']['alerts'][0];

interface AlertComparisonProps {
  alert: AlertFromAPI;
  entity: Client;
  onClose: () => void;
  onResolve: (alertId: string, isMatch: boolean, reason?: string) => void;
}

const AlertComparison: React.FC<AlertComparisonProps> = ({ 
  alert, 
  entity, 
  onClose, 
  onResolve 
}) => {
  const [decision, setDecision] = useState<'match' | 'no_match' | null>(null);
  const [reason, setReason] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  const { currentOrganization } = useOrganization();
  const organizationId = alert.organization_id || currentOrganization?.id || '';

  // Mutation for resolving alerts
  const resolveAlertMutation = $api.useMutation('patch', '/api/organization/{organization_id}/alerts/{alert_id}');

  // Initialize form with existing resolution if alert is already resolved
  useEffect(() => {
    if (alert.resolution_type) {
      setDecision(alert.resolution_type === 'match' ? 'match' : 'no_match');
    }
    if (alert.resolution_comment) {
      setReason(alert.resolution_comment);
    }
  }, [alert.resolution_type, alert.resolution_comment]);

  // Determine alert type from alert_type field
  const alertType = alert.alert_type;
  const isSanctioned = alertType === 'sanctioned' || alertType === 'sanction';
  const isPep = alertType === 'pep';
  const isAdverseMedia = alertType === 'adverse_media';

  const handleSubmit = async () => {
    if (!decision || !organizationId) return;
    
    try {
      setErrorMessage(null);
      setSuccessMessage(null);

      const resolutionType = decision === 'match' ? 'match' : 'false_positive';
      
      await resolveAlertMutation.mutateAsync({
        params: {
          path: {
            organization_id: organizationId,
            alert_id: alert.id,
          },
        },
        body: {
          resolution_type: resolutionType,
          resolution_comment: reason || undefined,
        },
      });

      setSuccessMessage(
        resolutionType === 'match' 
          ? 'Alerte confirmée comme correspondance' 
          : 'Alerte marquée comme fausse alerte'
      );
      
      // Call the onResolve callback after a short delay to show success message
      setTimeout(() => {
        onResolve(alert.id, decision === 'match', reason);
      }, 1500);
    } catch (error: unknown) {
      console.error('Error resolving alert:', error);
      const errorMsg = error && typeof error === 'object' && 'error' in error
        ? String(error.error)
        : 'Erreur lors de la résolution de l\'alerte';
      setErrorMessage(errorMsg);
    }
  };

  const getAlertTypeLabel = (type: string) => {
    switch (type) {
      case 'sanctioned':
      case 'sanction': return 'Sanction';
      case 'pep': return 'PEP';
      case 'adverse_media': return 'Médias adverses';
      default: return type;
    }
  };

  const getAlertTypeColor = (type: string) => {
    switch (type) {
      case 'sanctioned':
      case 'sanction': return 'bg-red-100 text-red-800';
      case 'pep': return 'bg-yellow-100 text-yellow-800';
      case 'adverse_media': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const isCompany = entity.company !== entity.name;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
            <AlertTriangle className="h-6 w-6 text-red-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Vérification d'alerte</h2>
              <div className="flex items-center space-x-2 mt-1">
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getAlertTypeColor(alert.alert_type)}`}>
                  {getAlertTypeLabel(alert.alert_type)}
                </span>
                <span className="text-sm text-gray-500">
                  {new Date(alert.date || alert.created_at).toLocaleDateString('fr-FR')}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Success/Error Messages */}
          {successMessage && (
            <div className="mb-4">
              <SuccessMessage message={successMessage} />
            </div>
          )}
          {errorMessage && (
            <div className="mb-4">
              <ErrorMessage error={errorMessage} />
            </div>
          )}

          {/* Alert Description */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-red-900 mb-2">Description de l'alerte</h3>
            <p className="text-red-800">{alert.description}</p>
          </div>

          {/* Comparison Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Données de notre plateforme */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                  {isCompany ? (
                    <Building2 className="h-5 w-5 text-blue-600" />
                  ) : (
                    <User className="h-5 w-5 text-blue-600" />
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-blue-900">Données de notre plateforme</h3>
                  <p className="text-blue-700">Informations renseignées</p>
                </div>
              </div>
              
              <div className="space-y-3">
                <div>
                  <span className="text-sm font-medium text-blue-800">Nom:</span>
                  <p className="text-blue-900">{entity.name}</p>
                </div>
                {entity.email && (
                  <div>
                    <span className="text-sm font-medium text-blue-800">Email:</span>
                    <p className="text-blue-900">{entity.email}</p>
                  </div>
                )}
                {entity.phone && (
                  <div>
                    <span className="text-sm font-medium text-blue-800">Téléphone:</span>
                    <p className="text-blue-900">{entity.phone}</p>
                  </div>
                )}
                {isCompany && entity.company && (
                  <div>
                    <span className="text-sm font-medium text-blue-800">Entreprise:</span>
                    <p className="text-blue-900">{entity.company}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Données détectées dans la liste publique */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <AlertTriangle className="h-10 w-10 text-red-600" />
                <div>
                  <h3 className="text-lg font-semibold text-red-900">Données détectées</h3>
                  <p className="text-red-700">{alert.list_name}</p>
                </div>
              </div>

              {isSanctioned && (
                <div className="space-y-3">
                  <div>
                    <span className="text-sm font-medium text-red-800">Nom correspondant:</span>
                    <p className="text-red-900">{alert.detected_name}</p>
                  </div>
                  {alert.detected_first_name && (
                    <div>
                      <span className="text-sm font-medium text-red-800">Prénom:</span>
                      <p className="text-red-900">{alert.detected_first_name}</p>
                    </div>
                  )}
                  {alert.detected_last_name && (
                    <div>
                      <span className="text-sm font-medium text-red-800">Nom de famille:</span>
                      <p className="text-red-900">{alert.detected_last_name}</p>
                    </div>
                  )}
                  {alert.detected_birth_date && (
                    <div>
                      <span className="text-sm font-medium text-red-800">Date de naissance:</span>
                      <p className="text-red-900">{alert.detected_birth_date}</p>
                    </div>
                  )}
                  {alert.detected_nationality && (
                    <div>
                      <span className="text-sm font-medium text-red-800">Nationalité:</span>
                      <p className="text-red-900">{alert.detected_nationality}</p>
                    </div>
                  )}
                  {alert.sanctioned_date && (
                    <div>
                      <span className="text-sm font-medium text-red-800">Date de sanction:</span>
                      <p className="text-red-900">{new Date(alert.sanctioned_date).toLocaleDateString('fr-FR')}</p>
                    </div>
                  )}
                  {alert.matching_percentage !== undefined && (
                    <div>
                      <span className="text-sm font-medium text-red-800">Pourcentage de correspondance:</span>
                      <p className="text-red-900">{alert.matching_percentage}%</p>
                    </div>
                  )}
                </div>
              )}

              {isPep && (
                <div className="space-y-3">
                  <div>
                    <span className="text-sm font-medium text-red-800">Nom correspondant:</span>
                    <p className="text-red-900">{alert.detected_name}</p>
                  </div>
                  {alert.detected_first_name && (
                    <div>
                      <span className="text-sm font-medium text-red-800">Prénom:</span>
                      <p className="text-red-900">{alert.detected_first_name}</p>
                    </div>
                  )}
                  {alert.detected_last_name && (
                    <div>
                      <span className="text-sm font-medium text-red-800">Nom de famille:</span>
                      <p className="text-red-900">{alert.detected_last_name}</p>
                    </div>
                  )}
                  {alert.detected_birth_date && (
                    <div>
                      <span className="text-sm font-medium text-red-800">Date de naissance:</span>
                      <p className="text-red-900">{alert.detected_birth_date}</p>
                    </div>
                  )}
                  {alert.detected_nationality && (
                    <div>
                      <span className="text-sm font-medium text-red-800">Nationalité:</span>
                      <p className="text-red-900">{alert.detected_nationality}</p>
                    </div>
                  )}
                  {alert.matching_percentage !== undefined && (
                    <div>
                      <span className="text-sm font-medium text-red-800">Pourcentage de correspondance:</span>
                      <p className="text-red-900">{alert.matching_percentage}%</p>
                    </div>
                  )}
                </div>
              )}

              {isAdverseMedia && (
                <div className="space-y-3">
                  <div>
                    <span className="text-sm font-medium text-red-800">Nom correspondant:</span>
                    <p className="text-red-900">{alert.detected_name}</p>
                  </div>
                  {alert.detected_first_name && (
                    <div>
                      <span className="text-sm font-medium text-red-800">Prénom:</span>
                      <p className="text-red-900">{alert.detected_first_name}</p>
                    </div>
                  )}
                  {alert.detected_last_name && (
                    <div>
                      <span className="text-sm font-medium text-red-800">Nom de famille:</span>
                      <p className="text-red-900">{alert.detected_last_name}</p>
                    </div>
                  )}
                  {alert.matching_percentage !== undefined && (
                    <div>
                      <span className="text-sm font-medium text-red-800">Pourcentage de correspondance:</span>
                      <p className="text-red-900">{alert.matching_percentage}%</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Decision Section */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Votre décision</h3>
              {alert.resolution_type && (
                <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                  alert.resolution_type === 'match' 
                    ? 'bg-red-100 text-red-800' 
                    : 'bg-green-100 text-green-800'
                }`}>
                  {alert.resolution_type === 'match' ? 'Correspondance confirmée' : 'Fausse alerte'}
                  {alert.resolution_date && ` - ${new Date(alert.resolution_date).toLocaleDateString('fr-FR')}`}
                </span>
              )}
            </div>
            
            <div className="space-y-4">
              <div className="flex space-x-4">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="decision"
                    value="match"
                    checked={decision === 'match'}
                    onChange={(e) => setDecision(e.target.value as 'match')}
                    className="h-4 w-4 text-red-600 focus:ring-red-500"
                  />
                  <div className="flex items-center space-x-2">
                    <XCircle className="h-5 w-5 text-red-600" />
                    <span className="font-medium text-red-900">C'est la même personne/entité</span>
                  </div>
                </label>
                
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="decision"
                    value="no_match"
                    checked={decision === 'no_match'}
                    onChange={(e) => setDecision(e.target.value as 'no_match')}
                    className="h-4 w-4 text-green-600 focus:ring-green-500"
                  />
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="font-medium text-green-900">Fausse alerte (homonyme)</span>
                  </div>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Commentaire (optionnel)
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Expliquez votre décision..."
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              disabled={resolveAlertMutation.isPending}
            >
              Annuler
            </button>
            <button
              onClick={handleSubmit}
              disabled={!decision || resolveAlertMutation.isPending || !organizationId}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {resolveAlertMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Traitement...</span>
                </>
              ) : (
                <span>Confirmer la décision</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlertComparison;